import { useBanners } from "@/hooks/useBanners";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import DOMPurify from "dompurify";
import { motion, AnimatePresence } from "framer-motion";
import { useSiteSettings } from "@/hooks/useSiteSettings";

interface BannerAdProps {
  size: "top" | "sidebar" | "article" | "footer" | "popup";
  className?: string;
}

const BannerAd = ({ size, className = "" }: BannerAdProps) => {
  const { data: banners, isLoading } = useBanners(size);
  const { data: settings } = useSiteSettings();
  const impressionTracked = useRef<Set<string>>(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);

  // Check if banners are enabled site-wide
  const bannersEnabled = settings?.find(s => s.key === 'banners_enabled')?.value === true;

  const dimensions = {
    top: { width: "970x250", height: "h-[250px]", aspectRatio: "aspect-[97/25]", pixelWidth: 970, pixelHeight: 250 },
    sidebar: { width: "300x600", height: "h-[600px]", aspectRatio: "aspect-[1/2]", pixelWidth: 300, pixelHeight: 600 },
    article: { width: "728x90", height: "h-[90px]", aspectRatio: "aspect-[728/90]", pixelWidth: 728, pixelHeight: 90 },
    footer: { width: "970x90", height: "h-[90px]", aspectRatio: "aspect-[97/9]", pixelWidth: 970, pixelHeight: 90 },
    popup: { width: "320x100", height: "h-[100px]", aspectRatio: "aspect-[16/5]", pixelWidth: 320, pixelHeight: 100 }
  };

  const { width, height, aspectRatio } = dimensions[size];

  const trackEvent = async (bannerId: string, eventType: 'impression' | 'click') => {
    try {
      await supabase.functions.invoke('track-banner-event', {
        body: {
          banner_id: bannerId,
          event_type: eventType,
          referrer: document.referrer,
        },
      });
    } catch (error) {
      console.error('Failed to track banner event:', error);
    }
  };

  const handleBannerClick = (bannerId: string) => {
    trackEvent(bannerId, 'click');
  };

  // Banner rotation effect
  useEffect(() => {
    if (!banners || banners.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % banners.length);
    }, 30000); // Rotate every 30 seconds
    
    return () => clearInterval(interval);
  }, [banners]);

  // Impression tracking effect
  useEffect(() => {
    if (!banners || banners.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const bannerId = entry.target.getAttribute('data-banner-id');
            if (bannerId && !impressionTracked.current.has(bannerId)) {
              impressionTracked.current.add(bannerId);
              trackEvent(bannerId, 'impression');
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    const bannerElements = document.querySelectorAll('[data-banner-id]');
    bannerElements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [banners, currentIndex]);

  // Always render placeholder with fixed height to prevent CLS
  // Don't render content if banners are disabled
  if (!bannersEnabled) {
    return (
      <div className={`${height} ${className}`} aria-hidden="true" />
    );
  }

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center bg-card/50 border border-border/50 rounded-lg ${height} ${className}`}>
        <div className="text-center">
          <span className="text-xs text-muted-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  if (!banners || banners.length === 0) {
    return (
      <div className={`flex items-center justify-center bg-card border border-border rounded-lg ${height} ${className}`}>
        <div className="text-center">
          <span className="text-xs text-muted-foreground">
            Banner Ad ({width})
          </span>
          <p className="text-xs text-muted-foreground mt-1">
            Contact for advertising
          </p>
        </div>
      </div>
    );
  }

  const currentBanner = banners && banners.length > 0 ? banners[currentIndex] : null;

  return (
    <div className={`w-full max-w-full overflow-hidden ${className}`}>
      <AnimatePresence mode="wait">
        {currentBanner && (
          <motion.div
            key={currentBanner.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            data-banner-id={currentBanner.id}
            className={`relative rounded-lg overflow-hidden w-full ${currentBanner.banner_type === 'image' ? `${height} ${aspectRatio}` : ''}`}
          >
            {currentBanner.banner_type === 'code' ? (
              <div 
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentBanner.code_content || '') }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-r from-orange-900/20 to-purple-900/20">
                {currentBanner.link_url ? (
                  <a 
                    href={currentBanner.link_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => handleBannerClick(currentBanner.id)}
                    className="w-full h-full flex items-center justify-center"
                  >
                    <img
                      src={currentBanner.image_url || ''}
                      alt={currentBanner.title}
                      className="w-full h-full object-contain"
                    />
                  </a>
                ) : (
                  <img
                    src={currentBanner.image_url || ''}
                    alt={currentBanner.title}
                    className="w-full h-full object-contain"
                  />
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BannerAd;
